import React from "react";
import { Printer } from "lucide-react";
import { faNum, faDigits, fmtJalaliDate } from "../lib/api";

// نمایش و چاپ پیش‌فاکتور به سبک نمونه (RTL، جدول خدمات، مبلغ به حروف)
export default function InvoiceView({ invoice, showPrint = true }) {
  if (!invoice) return null;
  const seller = invoice.seller || {};
  const buyer = invoice.buyer || {};
  const lines = invoice.lines || [];

  const cell = "border border-slate-300 px-2 py-1.5 text-xs";
  const label = "bg-slate-50 font-medium text-slate-700 " + cell;

  return (
    <div className="invoice-wrap">
      <style>{`
        @media print {
          body * { visibility: hidden !important; }
          .invoice-print, .invoice-print * { visibility: visible !important; }
          .invoice-print { position: absolute; inset: 0; margin: 0; box-shadow: none !important; }
          .no-print { display: none !important; }
        }
      `}</style>
      {showPrint && (
        <div className="no-print flex justify-end mb-3">
          <button
            type="button"
            onClick={() => window.print()}
            className="inline-flex items-center gap-2 bg-teal-600 hover:bg-teal-700 text-white text-sm px-4 py-2 rounded-xl"
          >
            <Printer size={16} /> چاپ / ذخیره PDF
          </button>
        </div>
      )}
      <div className="invoice-print bg-white border border-slate-200 rounded-2xl shadow-sm p-6 md:p-8 overflow-x-auto" dir="rtl">
        <div className="flex items-center justify-between border-b pb-4 mb-4">
          <div>
            <div className="text-lg font-bold text-slate-900">پیش فاکتور فروش کالا و خدمات</div>
            <div className="text-xs text-slate-500 mt-1">{seller.name || "—"}</div>
          </div>
          <div className="text-xs text-slate-600 space-y-1 text-left" dir="rtl">
            <div>شماره: {faDigits(invoice.invoice_no ?? "—")}</div>
            <div>تاریخ صدور: {fmtJalaliDate(invoice.issued_at)}</div>
            <div>مهلت پرداخت: {fmtJalaliDate(invoice.due_at)}</div>
            <div>دوره گزارش: {fmtJalaliDate(invoice.period_start)} تا {fmtJalaliDate(invoice.period_end - 1)}</div>
          </div>
        </div>

        <table className="w-full border-collapse mb-4">
          <tbody>
            <tr>
              <td className={label} style={{ width: "18%" }}>فروشنده</td>
              <td className={cell}>{seller.name || "—"}</td>
              <td className={label} style={{ width: "18%" }}>شناسه ملی</td>
              <td className={cell}>{faDigits(seller.national_id || "—")}</td>
            </tr>
            <tr>
              <td className={label}>کد اقتصادی</td>
              <td className={cell}>{faDigits(seller.economic_code || "—")}</td>
              <td className={label}>شماره ثبت</td>
              <td className={cell}>{faDigits(seller.registration_no || "—")}</td>
            </tr>
            <tr>
              <td className={label}>نشانی</td>
              <td className={cell} colSpan={2}>{seller.address || "—"}</td>
              <td className={cell}>{faDigits(seller.phone || "")}</td>
            </tr>
            <tr>
              <td className={label}>خریدار</td>
              <td className={cell}>{buyer.name || "—"}</td>
              <td className={label}>موبایل</td>
              <td className={cell}>{faDigits(buyer.mobile || "—")}</td>
            </tr>
          </tbody>
        </table>

        <table className="w-full border-collapse text-center">
          <thead>
            <tr className="bg-slate-100">
              <th className={cell}>ردیف</th>
              <th className={cell}>کد خدمت</th>
              <th className={cell}>شرح کالا یا خدمت</th>
              <th className={cell}>تعداد</th>
              <th className={cell}>مبلغ واحد (ریال)</th>
              <th className={cell}>مبلغ کل (ریال)</th>
            </tr>
          </thead>
          <tbody>
            {lines.length === 0 && (
              <tr>
                <td className={cell} colSpan={6}>در این دوره استعلامی ثبت نشده است</td>
              </tr>
            )}
            {lines.map((l, i) => (
              <tr key={i}>
                <td className={cell}>{faDigits(i + 1)}</td>
                <td className={cell}>{faDigits(l.code || "—")}</td>
                <td className={cell + " text-right"}>{l.title}</td>
                <td className={cell}>{faNum(l.count)}</td>
                <td className={cell}>{faNum(l.unit_price)}</td>
                <td className={cell}>{faNum(l.total)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <table className="w-full border-collapse mt-4 text-sm">
          <tbody>
            <tr>
              <td className={label} style={{ width: "30%" }}>جمع کل</td>
              <td className={cell}>{faNum(invoice.subtotal)} ریال</td>
            </tr>
            <tr>
              <td className={label}>تخفیف</td>
              <td className={cell}>{faNum(invoice.discount)} ریال</td>
            </tr>
            <tr>
              <td className={label}>مالیات و عوارض ({faDigits(invoice.tax_percent || 0)}٪)</td>
              <td className={cell}>{faNum(invoice.tax_amount)} ریال</td>
            </tr>
            <tr>
              <td className={label + " text-base"}>مبلغ قابل پرداخت</td>
              <td className={cell + " text-base font-bold text-teal-800"}>{faNum(invoice.total)} ریال</td>
            </tr>
          </tbody>
        </table>
        <div className="mt-3 text-sm text-slate-700">
          <span className="font-medium">مبلغ به حروف: </span>
          {invoice.total_words}
        </div>
      </div>
    </div>
  );
}
